import { ClientFactory } from "@/main/common/ClientFactory";
import { extractReadableMessageFromServer } from "@/main/common/ApiUtils";
import { createImageUrl } from "@/main/react/image/ImageCard";
import { getUserOwnedImageBasePath } from "@/main/util/ImageUrlHelper";
import { getClipPosterUrl, MAX_CLIP_DURATION_MS } from "@seavien/commons";
import type { Localizer } from "@/main/react/Types";

// Shared image-upload orchestration used by the picker uploader (ShadcnImageUploader) and the
// editor paste/drop path, so there is a single source of truth for the presign -> S3 -> mark
// sequence and the accepted-type / size contract.

export const ACCEPTED_IMAGE_MIME_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/tiff"] as const;
export const ACCEPTED_IMAGE_FORMAT_LABEL = "JPEG, PNG, GIF, WebP, TIFF";
export const MAX_IMAGE_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

/**
 * The two containers a phone actually produces — deliberately not a general video allowlist.
 *
 * MUST stay identical to `ImageService.ALLOWED_CLIP_CONTENT_TYPES` and to
 * `CreatePresignedUploadImageUrlRequest`'s `@Pattern`. There is no compile-time link between the three,
 * and the failure is asymmetric: a type listed here but not there is a file the user picks and the
 * presign then refuses.
 */
export const ACCEPTED_CLIP_MIME_TYPES = ["video/mp4", "video/quicktime"] as const;

/**
 * 50 MB, matching `ImageService.MAX_CLIP_UPLOAD_SIZE_BYTES` exactly rather than sitting under it.
 *
 * Note this is a different relationship from the image ceiling above, which is deliberately HALF the
 * backend's 20 MB. That slack is affordable for a photo and is not for a clip: 50 MB is already the
 * product ceiling a 30-second clip is sized against, so shaving it here would reject clips the backend
 * would have accepted, with no server message to explain why.
 */
export const MAX_CLIP_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB

/**
 * The part size the server would hand out (`ImageService.CLIP_MULTIPART_PART_SIZE_BYTES`). Only a fallback:
 * the size the upload actually slices on is what `create` RETURNED, so this cannot drift into a signed
 * `content-length` the server refuses.
 */
export const CLIP_MULTIPART_PART_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

/**
 * Attempts per PART, each on a fresh presign. This is the "resumable" in the resumable uploader
 * (design 2026-08-05 §6, D9): a part that fails is retried alone, the parts already accepted keep
 * their ETags and are never re-sent. Three is bounded on purpose, on top of the transport's own
 * replay of a dropped socket: a part that fails three fresh presigns in a row is not a blip, and the
 * member needs the error rather than a wait they cannot cancel.
 */
export const MAX_CLIP_PART_ATTEMPTS = 3;

const MAX_PERSISTED_IMAGE_NAME_LENGTH = 75;

export type UploadedMediaKind = "IMAGE" | "CLIP";

export type UploadedImageResult = {
    imagePublicId: string;
    name?: string;
    url?: string;
    thumbnailUrl?: string;
    createdAt?: string;
    /** "IMAGE" unless the backend says otherwise — see the note in the mark-uploaded branch. */
    mediaKind?: UploadedMediaKind;
    /** Null for an image, and also for a clip whose transcode has not finished measuring it. */
    durationMs?: number | null;
};

type PresignedUploadResponse = {
    presignedUrl?: string;
    imagePublicId?: string;
};

type MarkUploadedResponse = {
    imagePublicId?: string;
    name?: string;
    url?: string;
    thumbnailUrl?: string;
    createdAt?: string;
    mediaKind?: string;
    durationMs?: number | null;
};

export function isAcceptedImageType(type: string | undefined | null): boolean {
    return !!type && (ACCEPTED_IMAGE_MIME_TYPES as readonly string[]).includes(type);
}

export function isAcceptedClipType(type: string | undefined | null): boolean {
    return !!type && (ACCEPTED_CLIP_MIME_TYPES as readonly string[]).includes(type);
}

/**
 * THE gate (AGENTS.md rule 34). Every upload surface builds its accepted-type set from this one
 * function, so whether a surface may take a clip is checked in a single place and the next surface someone adds
 * inherits it rather than having to remember it.
 *
 * The failure this prevents is specific and unpleasant: a file picker that offers video against a
 * presign that refuses it. The user gets as far as choosing a clip, waiting, and then reading a
 * validation error about a file type the app itself put in front of them.
 *
 * Since 2026-09-12 clips are on in every environment (board-settle D21; `seavien.clips.enabled`, its
 * meta and its `MetaHandler` reader were deleted under rule 35), so the only question left is the
 * surface's own `allowClips`. Keep this the single place that question is asked.
 */
export type AcceptedUploadOptions = {
    /**
     * Whether THIS surface can use a clip at all.
     *
     * Defaults to true, so a surface has to opt out; the surfaces that must are the ones feeding an
     * image-sized URL — avatars, series covers, discussion card images.
     */
    allowClips?: boolean;
};

export function getAcceptedUploadMimeTypes({ allowClips = true }: AcceptedUploadOptions = {}): readonly string[] {
    return allowClips
        ? [...ACCEPTED_IMAGE_MIME_TYPES, ...ACCEPTED_CLIP_MIME_TYPES]
        : ACCEPTED_IMAGE_MIME_TYPES;
}

/** The `accept` attribute for a file input. Derived from the gate above, never hand-assembled. */
export function getAcceptedUploadAcceptAttribute(options?: AcceptedUploadOptions): string {
    return getAcceptedUploadMimeTypes(options).join(",");
}

/**
 * The human-readable format list for the "accepted formats are …" message.
 *
 * Derived from the same gate as the accept attribute so the two can never disagree — telling someone
 * MP4 is accepted while the picker filters it out is worse than not mentioning it. The labels are
 * container names, not localizable prose, so they are the same in every language.
 */
export function getAcceptedUploadFormatLabel(options?: AcceptedUploadOptions): string {
    return getAcceptedUploadMimeTypes(options).some((type) => type.startsWith("video/"))
        ? `${ACCEPTED_IMAGE_FORMAT_LABEL}, MP4, MOV`
        : ACCEPTED_IMAGE_FORMAT_LABEL;
}

/**
 * Throws a localized error when a clip is longer than the product ceiling; returns its measured
 * duration otherwise (null when it could not be measured).
 *
 * This is a courtesy, not the enforcement. The transcode Lambda's `TOO_LONG` is authoritative, and it
 * has to be — a browser measurement can be defeated and can simply be unavailable. What this buys is
 * the difference between finding out in a moment and finding out after uploading 50 MB on a phone.
 */
export async function rejectClipIfTooLong(
    file: File,
    { localizer }: { localizer: Localizer },
): Promise<number | null> {
    if (!isAcceptedClipType(file.type)) {
        return null;
    }
    const durationMs = await readClipDurationMs(file);
    if (durationMs != null && durationMs > MAX_CLIP_DURATION_MS) {
        throw new Error(localizer("typescript.clip.upload.too_long"));
    }
    return durationMs;
}

/** Whether this file may be offered/accepted at all, images and (when enabled) clips together. */
export function isAcceptedUploadType(type: string | undefined | null, options?: AcceptedUploadOptions): boolean {
    return !!type && getAcceptedUploadMimeTypes(options).includes(type);
}

/**
 * Whether a refused file looks like a HEIC/HEIF photo, so the refusal can name the real problem.
 *
 * Checks the FILENAME as well as the MIME type, and the order matters: the blank-`File.type` case is
 * precisely where the member gets the least useful error today, and there the extension is the only
 * signal left. Callers use this purely to pick better copy — it never widens what is accepted.
 *
 * Scoped to the refusal path on purpose. A stock iPhone photo usually never reaches here at all: iOS
 * Safari transcodes HEIC to JPEG on its way through `<input type="file">`. What this is for is Android
 * devices configured to shoot HEIF, where nothing intervenes.
 */
export function isLikelyHeicUpload(file: Pick<File, "name" | "type">): boolean {
    const type = (file.type ?? "").toLowerCase();
    if (type === "image/heic" || type === "image/heif") {
        return true;
    }
    return /\.hei[cf]$/i.test((file.name ?? "").trim());
}

/** The two clip containers by filename extension, for a file the browser handed over with no type. */
const CLIP_MIME_TYPE_BY_EXTENSION: Readonly<Record<string, (typeof ACCEPTED_CLIP_MIME_TYPES)[number]>> = {
    mp4: "video/mp4",
    mov: "video/quicktime",
};

/**
 * X-64 (round 44 A7): the clip type a BLANK-typed file's filename names, or null.
 *
 * The same signal `isLikelyHeicUpload` reads. Chrome for Android derives `File.type` from the picked item's
 * display name, so an item whose name gives no well-known type arrives with `type === ""`, and without this
 * a real `.mp4` was refused as an unsupported format. Blank types only: a file the browser DID type is never
 * re-typed from its name.
 */
export function inferBlankClipMimeType(file: Pick<File, "name" | "type">): string | null {
    if ((file.type ?? "").trim()) {
        return null;
    }
    const extension = /\.([a-z0-9]+)$/i.exec((file.name ?? "").trim())?.[1]?.toLowerCase();
    return extension ? CLIP_MIME_TYPE_BY_EXTENSION[extension] ?? null : null;
}

/**
 * The file to validate and upload: the original, or, for a blank-typed `.mp4`/`.mov` on a surface whose gate
 * accepts clips, the same bytes re-wrapped with the inferred type, so the local checks, the presign's
 * `contentType` and the S3 PUT all see one real type. A surface that opted out of clips gets the original
 * back and refuses it as before (short posts keep refusing clips).
 */
export function withInferredClipType(file: File, options?: AcceptedUploadOptions): File {
    const inferred = inferBlankClipMimeType(file);
    if (!inferred || !isAcceptedUploadType(inferred, options)) {
        return file;
    }
    return new File([file], file.name, { type: inferred, lastModified: file.lastModified });
}

/** The size ceiling that applies to this file — 50 MB for a clip, 10 MB for anything else. */
export function maxUploadSizeBytesForType(type: string | undefined | null): number {
    return isAcceptedClipType(type) ? MAX_CLIP_FILE_SIZE_BYTES : MAX_IMAGE_FILE_SIZE_BYTES;
}

/**
 * Reads a clip's duration by decoding just its metadata from an object URL.
 *
 * Resolves to null whenever the duration cannot be determined — an unreadable container, a stream
 * with no duration, a browser that refuses the codec. **Null must be treated as "allow"**, not as
 * "reject": this check exists to give a fast local error for the ordinary too-long case, and the
 * transcode Lambda's `TOO_LONG` is the authoritative one. Rejecting on an unreadable duration would
 * turn "we could not measure this in your browser" into a refusal the server would not have made.
 *
 * The object URL is revoked on every path, including the error and timeout ones. A leaked one pins
 * the whole file in memory, which for a 50 MB clip is not a rounding error.
 */
export function readClipDurationMs(file: File): Promise<number | null> {
    return new Promise((resolve) => {
        if (typeof document === "undefined" || typeof URL?.createObjectURL !== "function") {
            resolve(null);
            return;
        }

        const objectUrl = URL.createObjectURL(file);
        const video = document.createElement("video");
        let settled = false;

        const finish = (durationMs: number | null) => {
            if (settled) {
                return;
            }
            settled = true;
            window.clearTimeout(timeoutId);
            video.removeAttribute("src");
            URL.revokeObjectURL(objectUrl);
            resolve(durationMs);
        };

        // A browser that never fires either event would otherwise leave the caller awaiting forever,
        // holding the object URL open. Failing open after a bounded wait keeps the upload usable.
        const timeoutId = window.setTimeout(() => finish(null), 5_000);

        video.preload = "metadata";
        video.muted = true;
        video.onloadedmetadata = () => {
            const seconds = video.duration;
            // Infinity is what a stream with no known duration reports; NaN is an unreadable one.
            finish(Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds * 1000) : null);
        };
        video.onerror = () => finish(null);
        video.src = objectUrl;
    });
}

export function normalizeUploadImageName(imageName: string): string {
    const trimmed = imageName.trim();

    if (!trimmed) {
        return imageName;
    }

    const lastDotIndex = trimmed.lastIndexOf(".");
    let normalized = lastDotIndex > 0 ? trimmed.slice(0, lastDotIndex).trim() : trimmed;

    if (!normalized) {
        normalized = trimmed;
    }

    if (normalized.length > MAX_PERSISTED_IMAGE_NAME_LENGTH) {
        normalized = normalized.slice(0, MAX_PERSISTED_IMAGE_NAME_LENGTH).trim();
    }

    return normalized || trimmed;
}

// Which step of the presign -> S3 PUT -> mark-uploaded flow a failure came from. Attached to thrown
// errors and forwarded to the client logger so upload failures are diagnosable — the S3 PUT is
// cross-origin (never in backend logs) and presign rejections are logged only at DEBUG server-side,
// so this client-side step/status is often the only signal we get.
export type ImageUploadStep = "presign" | "s3-put" | "mark-uploaded";

/**
 * Whether the failing request got an HTTP response at all. A step label alone is not diagnosable for
 * the S3 PUT, because that request happens off-estate: an S3 403 (expired presign, clock skew), a
 * blocked CORS preflight and a dropped connection all surface identically to the caller and need
 * completely different fixes.
 */
export type ImageUploadTransport = "http-error" | "no-response";

export type AnnotatedUploadError = Error & {
    uploadStep?: ImageUploadStep;
    uploadStatus?: number;
    uploadTransport?: ImageUploadTransport;
    uploadReason?: string;
};

function extractHttpStatus(error: unknown): number | undefined {
    const status = (error as { response?: { status?: number } })?.response?.status;
    return typeof status === "number" ? status : undefined;
}

/**
 * Classifies an S3 PUT failure into a status + a BOUNDED reason vocabulary.
 *
 * <p>Two things make this more than a `Number(...)` call. First, `PresignedUploadApi.uploadFile`
 * encodes the outcome in `Error.message` — the HTTP status as a string on a non-2xx, but the raw
 * axios error when there was no response at all. `Number("Network Error")` is `NaN`, so before
 * 2026-08-07 the whole no-response class logged `imageUploadStep=s3-put` with **no status and no
 * other detail** — which is exactly what six prod failures looked like, leaving them undiagnosable.
 * `Number("")` is `0` and finite, so the empty-message case has to be excluded explicitly too.
 *
 * <p>Second, the reason is deliberately mapped to a closed set rather than passed through. The raw
 * message of a failed request can carry the request URL, and a presigned S3 URL contains
 * `X-Amz-Signature` — a credential. It must never reach a log line.
 */
function classifyUploadTransportFailure(error: unknown): {
    status?: number;
    transport: ImageUploadTransport;
    reason: string;
} {
    const message = (error as { message?: string })?.message;
    const trimmed = typeof message === "string" ? message.trim() : "";
    const status = trimmed.length > 0 ? Number(trimmed) : Number.NaN;
    if (Number.isFinite(status)) {
        return { status, transport: "http-error", reason: "http-error" };
    }

    const code = (error as { code?: string })?.code;
    const reason = code === "ERR_NETWORK" || code === "ECONNABORTED" || code === "ERR_CANCELED"
        ? code
        : "unknown";
    return { transport: "no-response", reason };
}

function annotateUploadError(
    error: unknown,
    step: ImageUploadStep,
    status?: number,
    transport?: ImageUploadTransport,
    reason?: string,
): AnnotatedUploadError {
    // Annotate the ORIGINAL error in place when it is an object (e.g. an AxiosError carrying
    // `response.data`), so callers can still recover the server's localized message via
    // getServerErrorMessage. Only wrap genuine primitives.
    const err: AnnotatedUploadError = (typeof error === "object" && error !== null)
        ? (error as AnnotatedUploadError)
        : new Error(String(error));
    err.uploadStep = step;
    if (typeof status === "number" && Number.isFinite(status)) {
        err.uploadStatus = status;
    }
    if (transport) {
        err.uploadTransport = transport;
    }
    if (reason) {
        err.uploadReason = reason;
    }
    return err;
}

/**
 * Extracts the step, HTTP status and transport outcome from a (possibly annotated) upload error for
 * structured logging. Callers spread the result into {@code logger.error(msg, err, diagnostics)} so
 * failures become queryable by {@code imageUploadStep} / {@code imageUploadStatus} /
 * {@code imageUploadTransport}.
 *
 * <p>{@code imageUploadTransport} is what makes an s3-put failure actionable: {@code http-error}
 * means S3 answered and refused (look at the presign — expiry, clock skew, headers), while
 * {@code no-response} means the request never completed (CORS, connectivity, a proxy stripping the
 * {@code x-amz-*} headers). Both used to log as a bare step label.
 */
export function getUploadErrorDiagnostics(error: unknown): {
    imageUploadStep?: ImageUploadStep;
    imageUploadStatus?: number;
    imageUploadTransport?: ImageUploadTransport;
    imageUploadReason?: string;
} {
    const annotated = error as AnnotatedUploadError;
    return {
        imageUploadStep: annotated?.uploadStep,
        imageUploadStatus: annotated?.uploadStatus ?? extractHttpStatus(error),
        imageUploadTransport: annotated?.uploadTransport,
        imageUploadReason: annotated?.uploadReason,
    };
}

export function getServerErrorMessage(error: unknown): string | undefined {
    const payload = (error as {
        response?: {
            data?: unknown;
        };
        data?: unknown;
    })?.response?.data ?? (error as { data?: unknown })?.data;

    const message = extractReadableMessageFromServer(payload);
    return typeof message === "string" && message.trim().length > 0
        ? message
        : undefined;
}

/**
 * Uploads a single image file through the backend presign -> S3 -> mark-uploaded flow and returns
 * the uploaded image DTO. Callers are responsible for any pre-upload validation they need (file
 * type, size, rotation); this function performs the network orchestration only.
 *
 * Throws a localized Error for missing-presign / S3 failures, and rethrows the raw backend error so
 * callers can surface a server validation message via {@link getServerErrorMessage}.
 */
export async function uploadImageFile(file: File, { localizer }: { localizer: Localizer }): Promise<UploadedImageResult> {
    // Clips only (A9): the resumable multipart lane exists for the one upload large enough to need it,
    // and images keep the single PUT below untouched. EVERY clip takes it, not only one over a part —
    // one lane per kind, so a small clip is simply the single-part case of the same path.
    if (isAcceptedClipType(file.type)) {
        return uploadClipMultipart(file, { localizer });
    }

    const restApi = ClientFactory.getAjaxRestControllerApi();
    const presignedUploadApi = ClientFactory.getPresignedUploadApi();

    const normalizedImageName = normalizeUploadImageName(file.name);

    let presignResponse: Awaited<ReturnType<typeof restApi.getUploadPresignedUrl>>;
    try {
        presignResponse = await restApi.getUploadPresignedUrl({
            contentType: file.type,
            imageName: normalizedImageName,
            sizeInBytes: file.size,
        });
    } catch (presignErr) {
        // Backend rejection (quota / rate-limit / validation). Preserve the original error so callers
        // can still surface the server's localized message via getServerErrorMessage; just tag the
        // step + HTTP status for diagnostics.
        throw annotateUploadError(presignErr, "presign", extractHttpStatus(presignErr));
    }

    const presignData = (presignResponse.data ?? {}) as PresignedUploadResponse;
    const presignedUrl = presignData.presignedUrl;
    const imagePublicId = presignData.imagePublicId;

    if (!presignedUrl || !imagePublicId) {
        throw annotateUploadError(new Error(localizer("typescript.image.upload.failed")), "presign");
    }

    try {
        await presignedUploadApi.uploadFile({
            presignedUrl,
            file,
            contentType: file.type,
            sizeInBytes: file.size,
        });
    } catch (uploadErr) {
        // The S3 PUT is cross-origin, so this failure never reaches our backend logs — what is tagged
        // here is the ONLY signal that the upload failed at all. Show the user a friendly, non-technical
        // message (copy rule 12); the transport detail rides on the error for logging, never displayed.
        const { status, transport, reason } = classifyUploadTransportFailure(uploadErr);
        throw annotateUploadError(
            new Error(localizer("typescript.image.upload.failed")),
            "s3-put",
            status,
            transport,
            reason,
        );
    }

    return markUploadedAndBuildResult(file, imagePublicId);
}

/**
 * The mark-uploaded step, shared by the single PUT and the multipart lane because the contract is the
 * SAME (A9): whatever wrote the object, `/image/mark/uploaded` is the state transition, and the transcoder
 * reads the key from the S3 event rather than from anything sent here.
 */
async function markUploadedAndBuildResult(file: File, imagePublicId: string): Promise<UploadedImageResult> {
    const restApi = ClientFactory.getAjaxRestControllerApi();
    try {
        const markUploadedResponse = await restApi.markImageAsUploadedRest({ imagePublicId });
        const markUploadedData = (markUploadedResponse?.data ?? {}) as MarkUploadedResponse;

        // Prefer what the server says the row IS over what the file looked like locally — the presign
        // is what decided the kind, and it is what chose the S3 prefix the object now lives on. Falling
        // back to the local content type keeps this correct against a server that predates the field.
        const mediaKind: UploadedMediaKind = markUploadedData.mediaKind === "CLIP"
            ? "CLIP"
            : markUploadedData.mediaKind === "IMAGE"
                ? "IMAGE"
                : (isAcceptedClipType(file.type) ? "CLIP" : "IMAGE");
        const isClip = mediaKind === "CLIP";

        return {
            imagePublicId: String(markUploadedData.imagePublicId ?? imagePublicId),
            name: markUploadedData.name ?? file.name,
            url: markUploadedData.url ?? getUserOwnedImageBasePath(imagePublicId),
            // A clip has NO /image/media/{id}/t variant — its only still is the poster — so sending it
            // through createImageUrl would hand every caller a URL that 404s.
            thumbnailUrl: markUploadedData.thumbnailUrl
                ?? (isClip ? getClipPosterUrl(imagePublicId) : createImageUrl(imagePublicId)),
            createdAt: markUploadedData.createdAt,
            mediaKind,
            durationMs: markUploadedData.durationMs ?? null,
        };
    } catch (markErr) {
        throw annotateUploadError(markErr, "mark-uploaded", extractHttpStatus(markErr));
    }
}

/**
 * The resumable multipart lane for a clip (design 2026-08-05 §6, reversed 2026-09-16: D9, D13, A9).
 *
 * create → for each part: presign + one PUT of exactly that byte range → complete with every ETag in
 * part order → mark-uploaded, unchanged. The three backend calls are the generated client's
 * (`/ajax/clip/upload/multipart/{create,part,complete}`), and the part size is what `create` returned,
 * so the slices can never disagree with the length the server signs into each part URL.
 *
 * The ceiling is checked FIRST, before any request: the picker already refuses an oversized clip, but
 * this is the service, and the paste/drop path reaches it without the picker. The server checks it
 * again at create and, unarguably, at S3 through the signed part length and the bounded part number.
 *
 * Error tagging keeps the vocabulary the single PUT uses (`presign` / `s3-put` / `mark-uploaded`),
 * so the diagnostics query that finds a failing clip upload today keeps finding it.
 */
async function uploadClipMultipart(file: File, { localizer }: { localizer: Localizer }): Promise<UploadedImageResult> {
    if (file.size > MAX_CLIP_FILE_SIZE_BYTES) {
        throw annotateUploadError(new Error(localizer("typescript.clip.upload.size.exceeded")), "presign");
    }

    const restApi = ClientFactory.getAjaxRestControllerApi();
    const presignedUploadApi = ClientFactory.getPresignedUploadApi();

    let created: Awaited<ReturnType<typeof restApi.createClipMultipartUpload>>;
    try {
        created = await restApi.createClipMultipartUpload({
            contentType: file.type,
            imageName: normalizeUploadImageName(file.name),
            sizeInBytes: file.size,
        });
    } catch (createErr) {
        // The server's refusals here are the single-PUT lane's, localized and specific (accepted
        // formats, ceiling, allowance, trust gate); preserve the error so getServerErrorMessage can
        // surface them, and tag the step exactly as the sibling does.
        throw annotateUploadError(createErr, "presign", extractHttpStatus(createErr));
    }

    const imagePublicId = created.data?.imagePublicId;
    const uploadId = created.data?.uploadId;
    const partSizeBytes = created.data?.partSizeBytes ?? CLIP_MULTIPART_PART_SIZE_BYTES;
    if (!imagePublicId || !uploadId || !(partSizeBytes > 0)) {
        throw annotateUploadError(new Error(localizer("typescript.image.upload.failed")), "presign");
    }

    const partCount = Math.max(1, Math.ceil(file.size / partSizeBytes));
    const completedParts: Array<{ partNumber: number; eTag: string }> = [];

    for (let partNumber = 1; partNumber <= partCount; partNumber += 1) {
        const start = (partNumber - 1) * partSizeBytes;
        const end = Math.min(partNumber * partSizeBytes, file.size);
        const part = file.slice(start, end);
        const contentLengthBytes = end - start;

        let lastFailure: unknown;
        let eTag: string | undefined;
        for (let attempt = 1; attempt <= MAX_CLIP_PART_ATTEMPTS && eTag === undefined; attempt += 1) {
            // A fresh presign per attempt: an expired or clock-skewed signature is the failure a
            // transport replay of the same URL can never recover from.
            let presignedUrl: string | undefined;
            try {
                const presigned = await restApi.presignClipMultipartPart({
                    imagePublicId,
                    uploadId,
                    partNumber,
                    contentLengthBytes,
                });
                presignedUrl = presigned.data?.presignedUrl;
            } catch (presignErr) {
                throw annotateUploadError(presignErr, "presign", extractHttpStatus(presignErr));
            }
            if (!presignedUrl) {
                throw annotateUploadError(new Error(localizer("typescript.image.upload.failed")), "presign");
            }

            try {
                eTag = await presignedUploadApi.uploadPart({ presignedUrl, part, contentLengthBytes });
            } catch (uploadErr) {
                lastFailure = uploadErr;
            }
        }

        if (eTag === undefined) {
            // The S3 PUT is cross-origin, so this tag is the only signal the upload failed at all; the
            // member sees the same calm copy as a failed single PUT.
            const { status, transport, reason } = classifyUploadTransportFailure(lastFailure);
            throw annotateUploadError(
                new Error(localizer("typescript.image.upload.failed")),
                "s3-put",
                status,
                transport,
                reason,
            );
        }
        completedParts.push({ partNumber, eTag });
    }

    try {
        await restApi.completeClipMultipartUpload({ imagePublicId, uploadId, parts: completedParts });
    } catch (completeErr) {
        // Complete is the server assembling what S3 already holds; a refusal here is a server answer
        // about this upload (ownership, a part S3 never saw), not a transport blip, so it is tagged with
        // the step the caller can act on and its status.
        throw annotateUploadError(completeErr, "s3-put", extractHttpStatus(completeErr), "http-error", "multipart-complete");
    }

    return markUploadedAndBuildResult(file, imagePublicId);
}

/**
 * Uploads one image as a MESSAGE ATTACHMENT.
 *
 * <p><b>Deliberately not `uploadImageFile` with a flag.</b> The two differ in exactly one thing that
 * never appears on the wire: where the bytes land. The ordinary path writes to the prefix CloudFront
 * serves; this one writes to a prefix no CloudFront behaviour serves, readable only through
 * `GET /ajax/chat/attachment/{publicId}` after a participation check. A boolean parameter would make
 * "publish this private picture to the CDN" a one-character mistake, so the two are separate functions
 * calling separate endpoints, and neither can be reached from the other by accident.
 *
 * <p>Everything else is identical and shared on purpose: the accepted-type gate, the size ceiling, the
 * three-step presign/PUT/confirm shape, and `annotateUploadError`'s step tagging. In particular the S3
 * PUT is cross-origin, so its failure never reaches our backend logs and the annotation is the only
 * signal the upload failed at all.
 *
 * <p>Images only. A clip content type is refused by the server rather than routed, because there is no
 * private clip lane and accepting one would store a permanently unreadable attachment.
 *
 * @returns the IMAGE public id to put in `SendChatMessagePayload.attachmentImagePublicIds`. Note this
 *          is NOT the id the transcript later renders: the server mints a per-attachment handle on
 *          send, so a sender cannot pre-compute the URL its recipient will fetch.
 */
export async function uploadChatAttachment(
    file: File,
    { localizer }: { localizer: Localizer },
): Promise<string> {
    const presignedUploadApi = ClientFactory.getPresignedUploadApi();

    let presignResponse: Awaited<ReturnType<typeof ClientFactory.createChatAttachmentUploadLink>>;
    try {
        presignResponse = await ClientFactory.createChatAttachmentUploadLink({
            contentType: file.type,
            imageName: normalizeUploadImageName(file.name),
            sizeInBytes: file.size,
        });
    } catch (presignErr) {
        // Preserve the original error: the server's refusals here are localized and specific (too many
        // images, images only, standing gate), and getServerErrorMessage is what surfaces them.
        throw annotateUploadError(presignErr, "presign", extractHttpStatus(presignErr));
    }

    const presignedUrl = presignResponse.data?.presignedUrl;
    const imagePublicId = presignResponse.data?.imagePublicId;
    if (!presignedUrl || !imagePublicId) {
        throw annotateUploadError(new Error(localizer("typescript.image.upload.failed")), "presign");
    }

    try {
        await presignedUploadApi.uploadFile({
            presignedUrl,
            file,
            contentType: file.type,
            sizeInBytes: file.size,
        });
    } catch (uploadErr) {
        const { status, transport, reason } = classifyUploadTransportFailure(uploadErr);
        throw annotateUploadError(
            new Error(localizer("typescript.image.upload.failed")),
            "s3-put",
            status,
            transport,
            reason,
        );
    }

    try {
        await ClientFactory.markChatAttachmentUploaded({ imagePublicId });
    } catch (markErr) {
        throw annotateUploadError(markErr, "mark-uploaded", extractHttpStatus(markErr));
    }
    return imagePublicId;
}
